import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
  flex: 1;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  color: #14464F;
  font-size: 2.5em;
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.2em;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  color: #14464F;
  font-size: 1.8em;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const SectionContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 30px;
`;

const Feature = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureTitle = styled.h3`
  color: #316C6E;
  font-size: 1.4em;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FeatureText = styled.p`
  color: #666;
  line-height: 1.6;
`;

const Emoji = styled.span`
  font-size: 1.5em;
`;

const AboutUsPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'About QuisqueyaQuest',
      subtitle: 'Discover the Beauty of the Dominican Republic',
      mission: 'Your ultimate travel companion in the Dominican Republic, designed for both locals and visitors to explore, create, and share unforgettable experiences.',
      discover: {
        title: '🌴 Discover & Explore',
        items: [
          {
            title: 'Find Hidden Gems 🗺️',
            description: 'Explore a curated collection of interesting places throughout the Dominican Republic, from popular tourist spots to hidden local treasures.'
          },
          {
            title: 'Plan Your Adventure 📱',
            description: 'Use our interactive map to discover nearby attractions, read reviews, and find detailed information about each location.'
          },
          {
            title: 'Local Insights 🇩🇴',
            description: 'Get authentic recommendations and insider tips about the best places to visit, eat, and stay.'
          }
        ]
      },
      create: {
        title: '✨ Create & Share',
        items: [
          {
            title: 'Custom Trips 🎨',
            description: 'Design your perfect itinerary by combining different locations and activities. Plan your route, set your schedule, and organize your perfect Dominican adventure.'
          },
          {
            title: 'Share with Friends 🤝',
            description: 'Share your custom trips with friends and family, or make them public to inspire other travelers in the community.'
          },
          {
            title: 'Travel Together 👥',
            description: 'Coordinate group trips, share expenses, and keep everyone on the same page with our collaborative features.'
          }
        ]
      },
      experience: {
        title: '🌟 Experience',
        items: [
          {
            title: 'Self-Guided Tours 🎯',
            description: 'Access our collection of pre-packaged travel routes, carefully designed to optimize your visit to different regions of the Dominican Republic.'
          },
          {
            title: 'Professional Tours 🎉',
            description: 'Book guided tours directly through our platform with verified local tour operators who provide unique and authentic experiences.'
          },
          {
            title: 'Secure Booking ✅',
            description: 'Easy and secure payment process through PayPal, with instant confirmation and direct communication with tour operators.'
          }
        ]
      }
    },
    es: {
      title: 'Sobre QuisqueyaQuest',
      subtitle: 'Descubre la Belleza de la República Dominicana',
      mission: 'Tu compañero definitivo de viaje en la República Dominicana, diseñado para que tanto locales como visitantes exploren, creen y compartan experiencias inolvidables.',
      discover: {
        title: '🌴 Descubre y Explora',
        items: [
          {
            title: 'Encuentra Joyas Ocultas 🗺️',
            description: 'Explora una colección seleccionada de lugares interesantes en toda la República Dominicana, desde lugares turísticos populares hasta tesoros locales escondidos.'
          },
          {
            title: 'Planifica tu Aventura 📱',
            description: 'Usa nuestro mapa interactivo para descubrir atracciones cercanas, leer reseñas y encontrar información detallada sobre cada ubicación.'
          },
          {
            title: 'Perspectivas Locales 🇩🇴',
            description: 'Obtén recomendaciones auténticas y consejos de expertos sobre los mejores lugares para visitar, comer y alojarte.'
          }
        ]
      },
      create: {
        title: '✨ Crea y Comparte',
        items: [
          {
            title: 'Viajes Personalizados 🎨',
            description: 'Diseña tu itinerario perfecto combinando diferentes lugares y actividades. Planifica tu ruta, establece tu horario y organiza tu perfecta aventura dominicana.'
          },
          {
            title: 'Comparte con Amigos 🤝',
            description: 'Comparte tus viajes personalizados con amigos y familia, o hazlos públicos para inspirar a otros viajeros en la comunidad.'
          },
          {
            title: 'Viaja en Grupo 👥',
            description: 'Coordina viajes grupales, comparte gastos y mantén a todos informados con nuestras funciones colaborativas.'
          }
        ]
      },
      experience: {
        title: '🌟 Experiencia',
        items: [
          {
            title: 'Tours Autoguiados 🎯',
            description: 'Accede a nuestra colección de rutas de viaje preestablecidas, cuidadosamente diseñadas para optimizar tu visita a diferentes regiones de la República Dominicana.'
          },
          {
            title: 'Tours Profesionales 🎉',
            description: 'Reserva tours guiados directamente a través de nuestra plataforma con operadores turísticos locales verificados que proporcionan experiencias únicas y auténticas.'
          },
          {
            title: 'Reserva Segura ✅',
            description: 'Proceso de pago fácil y seguro a través de PayPal, con confirmación instantánea y comunicación directa con los operadores turísticos.'
          }
        ]
      }
    }
  };

  const t = content[language];

  return (
    <PageWrapper>
      <Navbar hideMiddleSection />
      <Container>
        <HeroSection>
          <Logo src="/images/QQ-Icon.svg" alt="QuisqueyaQuest" />
          <Title>{t.title}</Title>
          <Subtitle>{t.subtitle}</Subtitle>
          <Subtitle style={{ marginTop: '20px' }}>{t.mission}</Subtitle>
        </HeroSection>

        <Section>
          <SectionTitle>{t.discover.title}</SectionTitle>
          <SectionContent>
            {t.discover.items.map((feature, index) => (
              <Feature key={index}>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureText>{feature.description}</FeatureText>
              </Feature>
            ))}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t.create.title}</SectionTitle>
          <SectionContent>
            {t.create.items.map((feature, index) => (
              <Feature key={index}>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureText>{feature.description}</FeatureText>
              </Feature>
            ))}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>{t.experience.title}</SectionTitle>
          <SectionContent>
            {t.experience.items.map((feature, index) => (
              <Feature key={index}>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureText>{feature.description}</FeatureText>
              </Feature>
            ))}
          </SectionContent>
        </Section>
      </Container>
    </PageWrapper>
  );
};

export default AboutUsPage; 